import { Injectable } from '@angular/core';
import { catchError, first, mergeMap, tap } from 'rxjs/operators';
import { BmtbillingService } from './api.service';
import { Observable, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { IBmtBillingState } from './state';
import { getEbillRequest } from './selector';
import { Lux } from '@roosevelt/common-ui-lib/core';

@Injectable({
  providedIn: 'root'
})
export class BmtBillingAsync {
  constructor(
    private readonly lux: Lux<{ billingState: IBmtBillingState }>,
    private readonly bmtbillingService: BmtbillingService,
  ) {
  }

  insertEbillContext(): Observable<any> {
    return this.lux.get(getEbillRequest).pipe(
      first(),
      tap(() =>
        this.lux.set(state => state.billingState, { isFetching: true })),
      mergeMap(x => this.bmtbillingService.insertEbillRecord(x)),
      tap(() => {
        this.lux.set(state => state.billingState, { isFetching: false });
      }),
      catchError((err: HttpErrorResponse) => {
        let errorMessage = '';
        if (err?.error?.apiErrorList?.length > 0) {
          errorMessage = err.error.apiErrorList.map(err => err.errorMessage).join('\n');
        }
        this.lux.set(state => state.billingState, { ebillError: errorMessage, isFetching: false });
        return of(errorMessage);
      })
    );
  }

  registerAchPayment(registerAchPaymentRequest): Observable<any> {
    return of(registerAchPaymentRequest).pipe(
      first(),
      tap(() =>
        this.lux.set(state => state.billingState, { isFetching: true })),
      mergeMap(x => this.bmtbillingService.registerAchPayment(x)),
      tap(() => {
        this.lux.set(state => state.billingState, { isFetching: false });
      }),
      catchError(err => {
        const errorMessage = this.getErrorMessage(err);
        this.lux.set(state => state.billingState, { achPaymentError: errorMessage, isFetching: false });
        return of(errorMessage);
      })
    );
  }

  registerAchPayDetails(registerAchPaymentRequest): Observable<any> {
    return of(registerAchPaymentRequest).pipe(
      first(),
      tap(() =>
        this.lux.set(state => state.billingState, { isFetchingAch: true, achPayError: '' })),
      mergeMap(x => this.bmtbillingService.registerAchPay(x)),
      tap(() => {
        this.lux.set(state => state.billingState, { isFetchingAch: false });
      }),
      catchError(err => {
        const errorMessage = this.getErrorMessage(err);
        this.lux.set(state => state.billingState, { achPayError: errorMessage, isFetching: false });
        return of(errorMessage);
      })
    );
  }

  getStates(countryCode): Observable<any> {
    return this.bmtbillingService.getStatesList(countryCode);
  }

  getCountries(): Observable<any> {
    return this.bmtbillingService.getCountriesList();
  }

  private getErrorMessage(err): string {
    const apiErrorList = err?.error?.apiErrorList;
    if (apiErrorList?.length > 0) {
      return apiErrorList.map(item => item.errorMessage).join('\n');
    }
    if (err?.name === 'TimeoutError') {
      return 'Timeout has occurred';
    }
    return 'Internal server error';
  }
}
